import { type RefObject, useEffect, useRef } from "react";
import { hostModalOpen } from "./host-modal-open";

/** Anything open on top of the panel that owns Escape itself (emoji / GIF picker, media
 *  lightbox, the conversation menu, the language list). The launcher peek is also a
 *  `dialog`, but it only shows while the panel is closed. */
const INNER_OVERLAY = '[role="dialog"]:not([data-wx-peek]), [role="listbox"], [role="menu"]';

type FocusRoot = Document | ShadowRoot;

/** An inner overlay that is really open: a hidden / inert / aria-hidden one (kept mounted for
 *  an exit animation, say) must not swallow Escape. */
function hasOpenOverlay(root: FocusRoot | null): boolean {
  if (!root) return false;
  return Array.from(root.querySelectorAll(INNER_OVERLAY)).some(
    (el) => !el.closest('[hidden], [inert], [aria-hidden="true"]'),
  );
}

function focusRoot(el: Element): FocusRoot | null {
  const root = el.getRootNode();
  return "activeElement" in root ? (root as FocusRoot) : null;
}

/**
 * Keyboard focus management for the open panel.
 *
 * - Opening moves focus into the panel, but only when focus is already inside the widget
 *   (the launcher or a peek button was used). The panel precedes the launcher in the DOM, so
 *   without this Tab leaves the widget, and an open that the host page triggered never
 *   steals focus.
 * - Closing from inside the panel returns focus to the launcher (the closed panel is
 *   `inert`, so the browser would otherwise drop focus to <body>).
 * - Escape closes the panel. In the Shadow DOM embeds only an Escape that originates in the
 *   widget counts (`composedPath()`), never one aimed at the host page; the iframe owns its
 *   whole document so any Escape there is the widget's. An open inner overlay keeps its Escape.
 */
export function usePanelFocus({
  isOpen,
  panelRef,
  onEscape,
  scopeToWidget,
}: {
  isOpen: boolean;
  panelRef: RefObject<HTMLElement | null>;
  onEscape: () => void;
  /** `true` when the widget shares a document with the host page (web-component / React). */
  scopeToWidget: boolean;
}): void {
  const onEscapeRef = useRef(onEscape);
  onEscapeRef.current = onEscape;
  const focusWasInPanel = useRef(false);
  const wasOpen = useRef(false);

  // Move focus in / give it back on open <-> closed transitions.
  useEffect(() => {
    const panel = panelRef.current;
    if (!panel) return;
    if (isOpen && !wasOpen.current) {
      const active = focusRoot(panel)?.activeElement;
      const fromLauncher =
        !!active && (active.matches("[data-wx-fab]") || !!active.closest("[data-wx-peek]"));
      if (fromLauncher) {
        panel.focus({ preventScroll: true });
        focusWasInPanel.current = true;
      }
    } else if (!isOpen && wasOpen.current && focusWasInPanel.current) {
      const fab = focusRoot(panel)?.querySelector<HTMLElement>("[data-wx-fab]");
      fab?.focus({ preventScroll: true });
      focusWasInPanel.current = false;
    }
    wasOpen.current = isOpen;
  }, [isOpen, panelRef]);

  // Track whether focus lives inside the panel (a blur to nothing keeps it: that is what
  // `inert` does when the panel closes).
  useEffect(() => {
    const panel = panelRef.current;
    if (!panel) return;
    const onIn = () => {
      focusWasInPanel.current = true;
    };
    // A press anywhere outside the widget means the visitor moved on to the host page, so
    // focus parked on <body> no longer belongs to the panel (see the Escape handler).
    const onPointerDown = (e: PointerEvent) => {
      const path = e.composedPath();
      const fab = focusRoot(panel)?.querySelector("[data-wx-fab]");
      if (!path.includes(panel) && !(fab && path.includes(fab))) focusWasInPanel.current = false;
    };
    const onOut = (e: FocusEvent) => {
      const next = e.relatedTarget as Node | null;
      if (next && !panel.contains(next)) focusWasInPanel.current = false;
    };
    panel.addEventListener("focusin", onIn);
    panel.addEventListener("focusout", onOut);
    document.addEventListener("pointerdown", onPointerDown, true);
    return () => {
      panel.removeEventListener("focusin", onIn);
      panel.removeEventListener("focusout", onOut);
      document.removeEventListener("pointerdown", onPointerDown, true);
    };
  }, [panelRef]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || e.defaultPrevented) return;
      // Escape that cancels an IME composition (CJK, etc.) belongs to the input method.
      // Some browsers report it with `isComposing` false and keyCode 229 instead.
      if (e.isComposing || e.keyCode === 229) return;
      // An input that gives Escape its own meaning (e.g. cancel the link field) opts out with
      // `data-wx-own-escape`; an inner field added later is safe without touching this hook.
      const origin = e.composedPath()[0];
      if (origin instanceof Element && origin.closest("[data-wx-own-escape]")) return;
      const panel = panelRef.current;
      if (!panel) return;
      if (scopeToWidget) {
        const fab = focusRoot(panel)?.querySelector("[data-wx-fab]");
        const path = e.composedPath();
        const inWidget = path.includes(panel) || !!(fab && path.includes(fab));
        // Focus can end up on <body> while the visitor is still in the panel: a picker that
        // closes (emoji-picker-react restores focus to the unfocusable shadow host) leaves
        // nothing focused. An Escape aimed at <body> then still belongs to the panel, as long as
        // the visitor's last interaction was inside it (see `onPointerDown`). Not while a host
        // modal is open: it can leave focus on <body> too, and its Escape is not ours.
        const origin = path[0];
        const focusLost =
          focusWasInPanel.current &&
          !hostModalOpen() &&
          (origin === document.body || origin === document.documentElement);
        if (!inWidget && !focusLost) return;
      }
      if (hasOpenOverlay(focusRoot(panel))) return;
      onEscapeRef.current();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [isOpen, panelRef, scopeToWidget]);
}
